import { useCallback } from "react";
import {
  useLocation,
  useNavigate,
  type SetURLSearchParams,
} from "react-router";

import { navigateAndForget } from "./navigateAndForget";

/**
 * Reflect an explicit turn navigation (j/k, header chevrons, go-to-turn bar)
 * in the URL via `?event=` so the position is shareable — the keyboard
 * analogue of an outline-link click. `replace` keeps the back button clean;
 * `?message=` is cleared (turn nav isn't message-scoped). Not called on
 * passive scroll, so the scroll-spy-driven turn label never churns the URL.
 * Shared by the inspect and scout transcript panels.
 */
export function useReflectEventNavigationInUrl(
  setSearchParams: SetURLSearchParams
): (eventId: string) => void {
  return useCallback(
    (eventId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("event", eventId);
          next.delete("message");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
}

/**
 * Focus-mode entry for a plain click: navigates in-window, from the SAME
 * `#`-prefixed href the anchor renders (modified clicks keep opening a new
 * tab natively). Idempotent: a double-click / f-key repeat must not push the
 * same focus URL twice (the first Back would then be a same-URL no-op).
 */
export function useOpenEventFocus(): (focusRoute: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (focusRoute: string) => {
      const route = focusRoute.startsWith("#")
        ? focusRoute.slice(1)
        : focusRoute;
      if (route === `${location.pathname}${location.search}`) return;
      navigateAndForget(navigate, route);
    },
    [navigate, location.pathname, location.search]
  );
}
